import { FirebaseAuthTypes, signOut as signOutMobile } from '@react-native-firebase/auth';
import { AxiosError } from 'axios';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { Auth, signOut as signOutWeb } from 'firebase/auth';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';

import { usePurchaseGifts } from '@/api/hooks/giftShowHooks';
import { useGetAllShows } from '@/api/hooks/showHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import TokneizedACHView from '@/components/paymentMethods/TokenizedACHView';
import TokenizedCardView from '@/components/paymentMethods/TokenizedCardView';
import Text from '@/components/Text';
import GiftShowCard from '@/components/trbShow/GiftShowCard';
import { APPSFLYER_CONFIG, GIFT_DISCOUNT_PERCENTAGE } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { auth } from '@/firebaseConfig';
import { GiftShowsData } from '@/lib/validations/GiftShowSchema';
import { applyDiscount } from '@/utils/applyDiscount';
import { decryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import { TokenizedACHData } from '../paymentMethods/achList';
import { TokenizedCreditCardData } from '../paymentMethods/creditCardList';

const ConfirmPurchaseGifts = () => {
  const navigation = useNavigation();

  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const { dispatch, clearCurrentUser } = useAuth();
  const { clearFavorites } = useMeFavorites();

  const [payloadState, setPayloadState] = useState<{
    tokenizedCard?: string;
    tokenizedACH?: string;
    fromIOS?: string;
    showId?: string;
    giftFormData?: string;
  }>();

  const { data: showData, isPending: allShowsPending } = useGetAllShows();

  const { mutateAsync: purchaseGifts, isPending } = usePurchaseGifts();
  const { showSuccess, showError } = useEndFlowModal();

  const [parsedTokenizedCard, setParsedTokenizedCard] = useState<TokenizedCreditCardData>();
  const [parsedTokenizedACH, setParsedTokenizedACH] = useState<TokenizedACHData>();
  const [parsedGiftFormData, setParsedGiftFormData] = useState<GiftShowsData>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        tokenizedCard?: string;
        tokenizedACH?: string;
        fromIOS?: string;
        showId?: string;
        giftFormData?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        setParsedTokenizedCard(safeJsonParse<TokenizedCreditCardData>(payloadObject?.tokenizedCard ?? ''));
        setParsedTokenizedACH(safeJsonParse<TokenizedACHData>(payloadObject?.tokenizedACH ?? ''));
        setParsedGiftFormData(safeJsonParse<GiftShowsData>(payloadObject?.giftFormData ?? ''));
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useLayoutEffect(() => {
    if (payloadState?.fromIOS) {
      navigation.setOptions({
        headerLeft: () => (
          <GoBackButton
            onPress={() => {
              dispatch({ type: 'SIGN_OUT' });
              clearCurrentUser();
              if (Platform.OS === 'web') {
                signOutWeb(auth() as Auth);
              } else {
                signOutMobile(auth() as FirebaseAuthTypes.Module);
              }
              clearFavorites();
              const params = new URLSearchParams({
                deep_link_value: 'purchaseShow',
                dismissBrowser: 'true'
              });
              const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
              Linking.openURL(backRoute);
            }}
          />
        )
      });
    }
  }, [clearCurrentUser, clearFavorites, dispatch, payloadState?.fromIOS, navigation]);

  const handleConfirm = () => {
    try {
      purchaseGifts(
        {
          key: parsedTokenizedCard?.card_key ?? parsedTokenizedACH?.check_key ?? '',
          showId: payloadState?.showId ?? '',
          paymentMethod: parsedTokenizedCard?.card_key !== undefined ? 'CREDIT_CARD' : 'ACH',
          phones: parsedGiftFormData?.gifts ?? []
        },
        {
          onSuccess: (_) => {
            if (!payloadState?.fromIOS) {
              showSuccess({
                title: i18n.t('giftShows.success_title'),
                message: i18n.t('giftShows.success_message')
              });
              router.dismissAll();
              router.push('/home/npoTabs');
              router.navigate({
                pathname: '/(app)/show/giftSummary',
                params: {
                  giftFormData: JSON.stringify(parsedGiftFormData) ?? ''
                }
              });
            } else {
              showSuccess({
                title: i18n.t('giftShows.success_title'),
                message: i18n.t('giftShows.success_message'),
                onButtonPress: () => {
                  dispatch({ type: 'SIGN_OUT' });
                  clearCurrentUser();
                  if (Platform.OS === 'web') {
                    signOutWeb(auth() as Auth);
                  } else {
                    signOutMobile(auth() as FirebaseAuthTypes.Module);
                  }
                  clearFavorites();
                  const params = new URLSearchParams({
                    deep_link_value: 'giftsList',
                    dismissBrowser: 'true',
                    giftFormData: JSON.stringify(parsedGiftFormData) ?? ''
                  });
                  const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
                  Linking.openURL(backRoute);
                }
              });
            }
          },
          onError: (error) => {
            const axiosError = error as AxiosError;

            const data = axiosError.response?.data as {
              usersHasPurchasedShow: boolean;
              phones: string[];
            };

            if (axiosError.status === 400 && data?.usersHasPurchasedShow) {
              const phonesWithShow: string[] = data.phones ?? [];

              const formattedList = phonesWithShow.map((phone) => `• ${phone}`).join('\n');

              const remainingGifts = (parsedGiftFormData?.gifts ?? []).filter(
                (gift) => !phonesWithShow.includes(gift.phone)
              );

              if (remainingGifts.length === 0) {
                showError({
                  title: i18n.t('giftShows.noRemainingPhones_title'),
                  message: i18n.t('giftShows.noRemainingPhones_message'),
                  onButtonPress: () => {
                    if (payloadState?.fromIOS) {
                      dispatch({ type: 'SIGN_OUT' });
                      clearCurrentUser();
                      if (Platform.OS === 'web') {
                        signOutWeb(auth() as Auth);
                      } else {
                        signOutMobile(auth() as FirebaseAuthTypes.Module);
                      }
                      clearFavorites();
                      const params = new URLSearchParams({
                        deep_link_value: 'giftSeries',
                        dismissBrowser: 'true'
                      });
                      const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
                      Linking.openURL(backRoute);
                    } else {
                      router.dismiss(2);
                    }
                  }
                });
              } else {
                showError({
                  title: i18n.t('giftShows.duplicatePhones_title'),
                  message: i18n.t('giftShows.duplicatePhones_message', { PHONES: formattedList }),
                  buttonLabel: i18n.t('giftShows.duplicatePhones_buttonLabel'),
                  onButtonPress: () => {
                    setParsedGiftFormData((prev) => ({
                      ...prev,
                      gifts: remainingGifts
                    }));
                  }
                });
              }
            } else {
              showError({});
            }
          }
        }
      );
    } catch (e) {
      console.error(e);
      if (payloadState?.fromIOS) {
        dispatch({ type: 'SIGN_OUT' });
        clearCurrentUser();
        if (Platform.OS === 'web') {
          signOutWeb(auth() as Auth);
        } else {
          signOutMobile(auth() as FirebaseAuthTypes.Module);
        }
        clearFavorites();
        const params = new URLSearchParams({
          deep_link_value: 'purchaseShow',
          dismissBrowser: 'true'
        });
        const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
        Linking.openURL(backRoute);
      }
    }
  };

  if (allShowsPending) {
    return (
      <SafeAreaView style={[styles.flexOne, styles.loaderContainer]} edges={['bottom']}>
        <DotsLoader />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <Text style={styles.title} fontWeight="700">
          {i18n.t('trb_show.about_to_gift')}
        </Text>
        <Text fontWeight="900" style={styles.showTitle}>
          {showData?.title}
        </Text>
        <Text>{showData?.description}</Text>

        {parsedGiftFormData?.gifts && (
          <FlatList
            data={parsedGiftFormData.gifts}
            keyExtractor={(item) => item.phone}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.giftGap}
            ItemSeparatorComponent={() => <Divider />}
            renderItem={({ item }) => (
              <GiftShowCard
                showThumbnail={showData?.thumbnail ?? '[]'}
                showPrice={showData?.price ?? '-'}
                firstName={item.firstName}
                lastName={item.lastName}
                phone={item.phone}
                textColor="gray"
              />
            )}
          />
        )}
        {showData?.price !== undefined && parsedGiftFormData?.gifts !== undefined && (
          <Text style={{ alignSelf: 'flex-end' }}>
            {i18n.t('giftShows.total_cost_prefix')} <Text fontWeight="900">{parsedGiftFormData.gifts.length}</Text>{' '}
            {i18n.t('giftShows.total_cost_middle')}{' '}
            <Text fontWeight="900">
              {`$${(+applyDiscount(parseFloat(showData.price), GIFT_DISCOUNT_PERCENTAGE) * parsedGiftFormData.gifts.length).toFixed(2)}`}
            </Text>
          </Text>
        )}
        <Text style={styles.taxes}>{i18n.t('trb_show.taxes_included')}</Text>

        {/* Payment Method */}
        <View style={styles.dataRow}>
          <Text style={styles.title} fontWeight="700">
            {i18n.t('donation_summary.payment_method')}
          </Text>
        </View>

        {parsedTokenizedCard && <TokenizedCardView tokenizedCard={parsedTokenizedCard} showDelete={false} />}
        {parsedTokenizedACH && <TokneizedACHView tokenizedAch={parsedTokenizedACH} showDelete={false} />}

        <BrandedButton onPress={handleConfirm} loading={isPending}>
          {i18n.t('mix.confirm')}
        </BrandedButton>
      </View>
    </SafeAreaView>
  );
};

export default ConfirmPurchaseGifts;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  loaderContainer: {
    justifyContent: 'center',
    alignItems: 'center'
  },

  container: {
    flex: 1,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0,
    paddingVertical: 18,
    gap: 15
  },

  title: {
    fontSize: 24,
    color: 'black'
  },

  showTitle: {
    fontSize: 28,
    color: 'black'
  },

  dataRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center'
  },

  data: {
    fontSize: 18,
    color: 'black'
  },

  giftGap: {
    gap: 5
  },

  taxes: {
    fontSize: 13,
    alignSelf: 'flex-end'
  }
});
